import { useState, useEffect, useCallback } from 'react'
import { Save, X, Circle } from 'lucide-react'
import { useEditContext } from '@/contexts/EditContext'
import { useToast } from '@/components/ui/Toast'
import { Toolbar } from '@/components/editor/Toolbar'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

export function EditorBar() {
  const {
    isEditMode,
    activeEditor,
    dirtySections,
    hasUnsavedChanges,
    publishAll,
    discardAll,
  } = useEditContext()

  const { showToast } = useToast()
  const [isPublishing, setIsPublishing] = useState(false)

  // Keyboard shortcut: Cmd/Ctrl+S to publish
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        if (hasUnsavedChanges && !isPublishing) {
          handlePublish()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasUnsavedChanges, isPublishing])

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const handlePublish = useCallback(async () => {
    setIsPublishing(true)
    try {
      await publishAll()
      showToast('success', 'Modifications publiees avec succes')
    } catch {
      showToast('error', 'Erreur lors de la publication')
    } finally {
      setIsPublishing(false)
    }
  }, [publishAll, showToast])

  const handleDiscard = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Etes-vous sur de vouloir annuler toutes les modifications ?'
      )
      if (confirmed) {
        discardAll()
      }
    }
  }, [hasUnsavedChanges, discardAll])

  if (!isEditMode) return null

  const dirtyCount = dirtySections.size

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-white border-t shadow-lg',
        'transition-transform duration-300',
        isEditMode ? 'translate-y-0' : 'translate-y-full'
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Left: Toolbar (when richtext is focused) */}
          <div className="flex-1 min-w-0">
            {activeEditor ? (
              <div className="flex items-center gap-1 overflow-x-auto">
                <Toolbar editor={activeEditor} />
              </div>
            ) : (
              <span className="text-sm text-gray-500">
                Mode edition actif
              </span>
            )}
          </div>

          {/* Right: Status and actions */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Unsaved indicator */}
            {hasUnsavedChanges && (
              <div className="flex items-center gap-2 text-amber-600">
                <Circle className="w-2 h-2 fill-current" />
                <span className="text-sm whitespace-nowrap">
                  {dirtyCount} modification{dirtyCount > 1 ? 's' : ''} non publiee{dirtyCount > 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDiscard}
                disabled={!hasUnsavedChanges || isPublishing}
              >
                <X className="w-4 h-4 mr-1" />
                Annuler
              </Button>

              <Button
                variant="primary"
                size="sm"
                onClick={handlePublish}
                disabled={!hasUnsavedChanges || isPublishing}
              >
                <Save className="w-4 h-4 mr-1" />
                {isPublishing ? 'Publication...' : 'Publier'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
